import { lastAssistantMessageIsCompleteWithToolCalls, type UIMessage } from 'ai';
import { toolDefinitions } from '@tiptap-pro/ai-toolkit-ai-sdk';
import { EDITOR_WRITE_TOOL_NAMES } from '@flintmint/core-shared/utils/editor-write-tool';
import type { ClassifierResult } from '@flintmint/core-shared/medical-note';
import type { MedicalClassificationMessageMetadata } from '@flintmint/core-shared/message-metadata';

export interface ToolStreamingPart {
  input: unknown;
  output?: unknown;
  state: string;
  type: string;
  toolCallId: string;
}

type DisplayMessageRole = 'user' | 'assistant';
type MedicalClassifierResult = Extract<ClassifierResult, { type: 'medical' }>;

interface ChatMessageBase {
  id: string;
  role: DisplayMessageRole;
  sourceMessageId: string;
  text: string;
  createdAt: number;
}

interface TextChatMessage extends ChatMessageBase {
  kind: 'text';
}

interface MedicalClassificationChatMessage extends ChatMessageBase {
  kind: 'medical_classification';
  role: 'assistant';
  turnId: string;
  classification: MedicalClassifierResult;
}

export type ChatMessage = TextChatMessage | MedicalClassificationChatMessage;

export type ChatTranscript = {
  updateFromMessages(messages: UIMessage[]): ChatMessage[];
  reset(): void;
};

export const createChatTranscript = (initialMessages: ChatMessage[] = []): ChatTranscript => {
  let chatMessages = initialMessages;

  return {
    updateFromMessages(messages) {
      chatMessages = buildChatMessages(messages, chatMessages);
      return chatMessages;
    },
    reset() {
      chatMessages = [];
    },
  };
};

export const getChatMessagesKey = (chatMessages: ChatMessage[]): string =>
  JSON.stringify(chatMessages.map(({ id, text }) => [id, text]));

export const TIPTAP_TOOL_NAMES = new Set(Object.keys(toolDefinitions()));
export const STREAMED_EDITOR_TOOL_NAMES = new Set([...TIPTAP_TOOL_NAMES, ...EDITOR_WRITE_TOOL_NAMES]);

export const getToolParts = (message: UIMessage): ToolStreamingPart[] =>
  (message.parts?.filter((p: any) => p.type?.startsWith('tool-')) ?? []) as ToolStreamingPart[];

export const getLastToolPart = (message: UIMessage): ToolStreamingPart | undefined => {
  const parts = getToolParts(message);
  return parts[parts.length - 1];
};

export const getMessageMedicalClassification = (message: UIMessage): ClassifierResult | null => {
  const metadata = message.metadata as MedicalClassificationMessageMetadata | undefined;
  return metadata?.medicalClassification ?? null;
};

const buildChatMessages = (messages: UIMessage[], previousItems: ChatMessage[] = []): ChatMessage[] => {
  const previousClassificationByTurnId = new Map<string, MedicalClassificationChatMessage>();
  const previousItemsById = new Map<string, ChatMessage>();

  for (const item of previousItems) {
    previousItemsById.set(item.id, item);
    if (item.kind === 'medical_classification' && !previousClassificationByTurnId.has(item.turnId)) {
      previousClassificationByTurnId.set(item.turnId, item);
    }
  }

  const displayItems: ChatMessage[] = [];
  const emittedClassificationIds = new Set<string>();
  let currentTurnId: string | null = null;
  let lastClassificationKey: string | null = null;

  for (const message of messages) {
    if (message.role === 'user') {
      currentTurnId = message.id;
      appendTextDisplayItem(displayItems, message, 'user', previousItemsById);
      continue;
    }

    if (message.role !== 'assistant') continue;

    const classificationItem = getMedicalClassificationDisplayItem({
      message,
      turnId: currentTurnId ?? message.id,
      previousClassificationByTurnId,
    });

    if (
      classificationItem &&
      !emittedClassificationIds.has(classificationItem.id) &&
      getClassificationKey(classificationItem.classification) !== lastClassificationKey
    ) {
      displayItems.push(classificationItem);
      emittedClassificationIds.add(classificationItem.id);
      lastClassificationKey = getClassificationKey(classificationItem.classification);
    }

    appendTextDisplayItem(displayItems, message, 'assistant', previousItemsById);
  }

  return displayItems;
};

const getClassificationKey = (classification: MedicalClassifierResult): string => {
  const { flowId, data } = classification;
  const { chiefComplaint, otherModules } = data;
  return JSON.stringify([flowId, chiefComplaint, [...(otherModules ?? [])].sort()]);
};

export const getDisplayText = (message: UIMessage): string => {
  const textParts = message.parts?.filter((part) => part.type === 'text') ?? [];
  return textParts.map((part) => part.text).join('');
};

export const shouldAutoSend = ({ messages }: { messages: UIMessage[] }): boolean =>
  lastAssistantMessageIsCompleteWithToolCalls({ messages });

const appendTextDisplayItem = (
  displayItems: ChatMessage[],
  message: UIMessage,
  role: DisplayMessageRole,
  previousItemsById: Map<string, ChatMessage>
): void => {
  const bodyText = getDisplayText(message);
  if (!bodyText) return;

  const id = `${message.id}-text`;
  const previousItem = previousItemsById.get(id);

  displayItems.push({
    id,
    kind: 'text',
    role,
    sourceMessageId: message.id,
    text: bodyText,
    createdAt: previousItem?.createdAt ?? Date.now(),
  });
};

const getMedicalClassificationDisplayItem = ({
  message,
  turnId,
  previousClassificationByTurnId,
}: {
  message: UIMessage;
  turnId: string;
  previousClassificationByTurnId: Map<string, MedicalClassificationChatMessage>;
}): MedicalClassificationChatMessage | null => {
  const previousClassification = previousClassificationByTurnId.get(turnId);
  if (previousClassification) {
    return previousClassification.sourceMessageId === message.id ? previousClassification : null;
  }

  const metadata = message.metadata as MedicalClassificationMessageMetadata | undefined;
  const classification = metadata?.medicalClassification;

  if (classification?.type !== 'medical' || !metadata?.showMedicalClassificationBubble) return null;

  return {
    id: `medical-classification:${turnId}`,
    kind: 'medical_classification',
    role: 'assistant',
    sourceMessageId: message.id,
    turnId,
    classification,
    text: getMedicalClassificationText(classification),
    createdAt: Date.now(),
  };
};

const getMedicalClassificationText = (classification: MedicalClassifierResult): string => {
  const { chiefComplaint, otherModules } = classification.data;
  const subject = chiefComplaint ?? `a ${classification.flowName} note`;
  const modules = otherModules?.length ? otherModules.join(', ') : '';
  return modules ? `Looks like ${subject} with ${modules}` : `Looks like ${subject}`;
};
